import { Button } from '../../../components/Button';
import { localDateString } from './attendanceUi';

export interface DateRange { from: string; to: string }

const fieldClass = 'h-9 rounded-lg border border-input bg-background px-3 text-sm text-foreground outline-none focus:ring-2 focus:ring-ring/30';

const pad = (n: number) => String(n).padStart(2, '0');
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** Named presets, all ending today or at a month boundary. */
export function rangePreset(key: 'thisMonth' | 'lastMonth' | 'last3Months'): DateRange {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    if (key === 'lastMonth') {
        return { from: ymd(new Date(y, m - 1, 1)), to: ymd(new Date(y, m, 0)) };
    }
    if (key === 'last3Months') {
        return { from: ymd(new Date(y, m - 2, 1)), to: localDateString() };
    }
    return { from: ymd(new Date(y, m, 1)), to: localDateString() }; // thisMonth
}

const PRESETS: { key: 'thisMonth' | 'lastMonth' | 'last3Months'; label: string }[] = [
    { key: 'thisMonth', label: 'This month' },
    { key: 'lastMonth', label: 'Last month' },
    { key: 'last3Months', label: 'Last 3 months' },
];

const matchesPreset = (r: DateRange, key: 'thisMonth' | 'lastMonth' | 'last3Months') => {
    const p = rangePreset(key);
    return p.from === r.from && p.to === r.to;
};

/**
 * Period picker for attendance reports: quick presets plus free from/to dates.
 * The caller clamps nothing — the API caps the range at one year.
 */
export function ReportRangePicker({ value, onChange }: { value: DateRange; onChange: (r: DateRange) => void }) {
    const today = localDateString();
    return (
        <div className="flex flex-wrap items-center gap-2">
            <div className="flex flex-wrap gap-1">
                {PRESETS.map((p) => {
                    const active = matchesPreset(value, p.key);
                    return (
                        <Button key={p.key} size="sm" variant={active ? 'secondary' : 'ghost'} onClick={() => onChange(rangePreset(p.key))}>
                            {p.label}
                        </Button>
                    );
                })}
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <input className={fieldClass} type="date" value={value.from} max={value.to || today}
                    onChange={(e) => onChange({ ...value, from: e.target.value })} aria-label="From date" />
                <span>to</span>
                <input className={fieldClass} type="date" value={value.to} min={value.from} max={today}
                    onChange={(e) => onChange({ ...value, to: e.target.value })} aria-label="To date" />
            </div>
        </div>
    );
}
